/**
 * @descripton renders the list of categories that the knowledge center entries are grouped on. Selecting a
 *  category from the list renders those knowledge entries onto the page
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import keyBy from 'lodash/keyBy';

import { CATEGORIES } from '../constants';

const styles = {
  button: {
    border: 1,
    borderColor: 'grey.400'
  },
  list: {
    flexWrap: 'wrap',
    gap: 2
  },
  item: {
    width: '19rem',
    p: 0
  },
  text: {
    wordBreak: 'break-word'
  }
};

const CATEGORY_MAP = keyBy(CATEGORIES, (entry) => entry.value);

/**
 * @prop {string[]}} categoryList - a list of categories to display
 * @prop {boolean} isLoading - indicates if the page is still loading
 * @prop {(category: string) => void} onCategoryClick - callback function when a category is clicked
 * @prop {string} selectedCategory - indicates which category has been selected
 */
const CategoryList = ({
  categoryList,
  isLoading,
  onCategoryClick,
  selectedCategory
}) => (isLoading ? (
  <List component={Stack} direction='row' sx={styles.list}>
    <ListItem sx={styles.item}>
      <Skeleton animation='wave' variant='rectangular' width='100%' height={50} />
    </ListItem>
    <ListItem sx={styles.item}>
      <Skeleton animation='wave' variant='rectangular' width='100%' height={50} />
    </ListItem>
    <ListItem sx={styles.item}>
      <Skeleton animation='wave' variant='rectangular' width='100%' height={50} />
    </ListItem>
  </List>
) : (
  <List component={Stack} direction='row' sx={styles.list}>
    {categoryList?.map((category) => {
      const { label, Icon } = CATEGORY_MAP[category];

      return (
        <ListItem sx={styles.item} key={category}>
          <ListItemButton
            sx={styles.button}
            onClick={() => onCategoryClick(category)}
            selected={category === selectedCategory}
          >
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText primary={label} sx={styles.text} />
          </ListItemButton>
        </ListItem>
      );
    })}
  </List>
));

CategoryList.propTypes = {
  categoryList: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool.isRequired,
  onCategoryClick: PropTypes.func.isRequired,
  selectedCategory: PropTypes.string
};

CategoryList.defaultProps = {
  categoryList: null,
  selectedCategory: null
};

export default CategoryList;
